/** A pointer position in client coordinates. */
export type DragPosition = { x: number; y: number };
/** Normal release, existing cancellation, or resource-only invalidation. */
export type DragFinishReason = "release" | "cancel" | "invalidate";
/** An exact owner; false movement means it has completed, without discovery of a replacement. */
export interface GestureOwner {
	isCurrent(): boolean;
	move(x: number, y: number): boolean;
	finish(x: number, y: number): void;
	invalidate(): void;
}

/** Delivers a captured drag independently of GPU requests and serializes reentrant input. */
export class CapturedDragGesture {
	private _state: "active" | "consumed" | "finished" = "active";
	private _positions: DragPosition[] = [];
	private _terminal?: { position: DragPosition; reason: DragFinishReason };
	private _draining = false;
	private _last: DragPosition;
	constructor(
		private _owner: GestureOwner,
		private _releaseCamera: () => void,
		initial: DragPosition,
	) {
		this._last = initial;
	}
	get isLive(): boolean {
		return this._state !== "finished";
	}
	get isConsumed(): boolean {
		return this._state === "consumed";
	}
	move(position: DragPosition): void {
		if (this._state !== "active" || this._terminal) {
			return;
		}
		this._positions.push(position);
		this._drain();
	}
	finish(position: DragPosition, reason: DragFinishReason): void {
		if (!this.isLive || this._terminal) {
			return;
		}
		this._terminal = { position, reason };
		if (reason !== "release") {
			this._positions.length = 0;
		}
		this._drain();
	}
	private _deliver(position: DragPosition): void {
		if (!this._owner.isCurrent()) {
			this._complete(position, "invalidate");
			return;
		}
		this._last = position;
		if (!this._owner.move(position.x, position.y)) {
			this._state = "consumed";
			this._positions.length = 0;
		}
	}
	private _drain(): void {
		if (this._draining) {
			return;
		}
		this._draining = true;
		try {
			while (this._positions.length && this._state === "active") {
				this._deliver(this._positions.shift()!);
			}
			if (this.isLive && this._terminal) {
				const { position, reason } = this._terminal;
				if (
					reason === "release" &&
					this._state === "active" &&
					(position.x !== this._last.x || position.y !== this._last.y)
				) {
					this._deliver(position);
				}
				if (this.isLive) {
					this._complete(this._last, reason);
				}
			}
		} catch (error) {
			if (this.isLive) {
				this._complete(this._last, "invalidate");
			}
			throw error;
		} finally {
			this._draining = false;
		}
	}
	private _complete(position: DragPosition, reason: DragFinishReason): void {
		const consumed = this.isConsumed;
		this._state = "finished";
		this._positions.length = 0;
		this._terminal = undefined;
		try {
			if (!consumed) {
				if (reason === "invalidate" || !this._owner.isCurrent()) {
					this._owner.invalidate();
				} else {
					try {
						this._owner.finish(position.x, position.y);
					} catch (error) {
						this._owner.invalidate();
						throw error;
					}
				}
			}
		} finally {
			this._releaseCamera();
		}
	}
}
